import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, MenuItem, Select } from '@mui/material'
import { CheckBoxOutlineBlank, ArrowDropDown } from '@mui/icons-material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { colors } from 'ui/theme'
import { StartEnd } from 'ui/utils/AvailableDateTimes'
import { DayOfWeekCheckStatus } from './RegularReservationFrameSetting'
import { ReservationTimeFrameProps } from 'types/med'
import getDateLocale from 'ui/utils/getDateLocale'

const useStyles = makeStyles()((theme: Theme) => ({
  column: {
    border: `0 solid ${theme.palette.divider}`,
    '&:not(:first-child)': {
      borderLeftWidth: '1px',
    },
  },
  timeColumn: {
    width: '48px',
  },
  cell: {
    display: 'flex',
    alignItems: 'center',
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: theme.palette.divider,
    fontSize: '12px',
    padding: theme.spacing(1),
    height: '44px',
    boxSizing: 'border-box',
    letterSpacing: '0.5px',
    '&:not(:first-child)': {
      borderTopWidth: '1px',
    },
  },
  cellCentered: {
    justifyContent: 'center',
  },
  cellNoSelection: {
    backgroundColor: colors.Gray1, // TODO: この箇所のみtheme.paletteから色を取得できないので修正
    color: theme.palette.text.disabled,
  },
  cellDropdown: {
    flex: 1,
    fontSize: '12px',
    lineHeight: '24px',
    marginLeft: theme.spacing(-1),
    '>.MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  cellDropdownNoSelection: {
    color: theme.palette.action.disabledBackground,
  },
  valueCheckBox: {
    marginLeft: theme.spacing(-1),
  },
  dayOfWeekCheckbox: {
    marginLeft: theme.spacing(-1),
  },
  dummySelect: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 0.875, 2, 1),
    cursor: 'pointer',
    div: {
      flexWrap: 'nowrap',
      flexShrink: 0,
    },
    '.MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
    },
  },
}))

export const ReservationFrameTimes: React.FC<{ values: StartEnd[] }> = ({
  values,
}) => {
  const { classes } = useStyles()
  return (
    <ReservationFrameColumn className={classes.timeColumn}>
      {values.map((startEnd) => (
        <ReservationFrameCell key={startEnd.start} center>
          {startEnd.start}
        </ReservationFrameCell>
      ))}
    </ReservationFrameColumn>
  )
}

export const RegularReservationFrameDayOfWeek: React.FC<{
  dayOfWeek: number
  checkStatusesOfDay: DayOfWeekCheckStatus
  setDayOfWeekChecked: (checked: boolean) => void
  startEnds: StartEnd[]
  readOnly?: boolean
}> = ({
  dayOfWeek,
  checkStatusesOfDay,
  setDayOfWeekChecked,
  startEnds,
  readOnly,
}) => {
  const { classes } = useStyles()
  const dateLocale = getDateLocale()

  const [checked, indeterminate] = useMemo(() => {
    const checkedLength = Object.values(checkStatusesOfDay).filter(
      (checked) => checked
    ).length
    const anyChecked = checkedLength > 0
    const notAllChecked = checkedLength !== startEnds.length
    return [anyChecked, anyChecked && notAllChecked]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkStatusesOfDay])

  return (
    <ReservationFrameColumn>
      <ReservationFrameCell component={'label'} center={readOnly}>
        {!readOnly && (
          <Checkbox
            color="primary"
            checked={checked}
            indeterminate={indeterminate}
            onChange={(_, checked) => setDayOfWeekChecked(checked)}
            icon={<CheckBoxOutlineBlank color="action" />}
            className={classes.dayOfWeekCheckbox}
          />
        )}
        {dateLocale.localize?.day(dayOfWeek, { width: 'abbreviated' })}
      </ReservationFrameCell>
    </ReservationFrameColumn>
  )
}

export const ReservationFrameCell: React.FC<{
  className?: string
  children?: React.ReactNode
  center?: boolean
  component?: 'div' | 'label'
}> = ({ className, center, children, component = 'div' }) => {
  const { classes, cx } = useStyles()
  const props = {
    className: cx(classes.cell, center && classes.cellCentered, className),
    children,
  }
  if (component === 'label') {
    return <label {...props} />
  }
  return <div {...props} />
}

export const ReservationFrameColumn: React.FC<{
  className?: string
  children?: React.ReactNode
}> = ({ className, children }) => {
  const { classes, cx } = useStyles()
  return <div className={cx(classes.column, className)}>{children}</div>
}

export const ReservationFrameSettingCellReadOnly: React.FC<{
  number: number
}> = ({ number }) => {
  const { t } = useTranslation()

  const { classes, cx } = useStyles()
  return (
    <ReservationFrameCell
      className={cx(!number && classes.cellNoSelection)}
      center
    >
      {number > 0 ? `${number}${t('人', { count: number })}` : t('なし')}
    </ReservationFrameCell>
  )
}

//NOTE: Mui-Selectコンポーネントを大量に描画すると時間がかかっていたので同じ表示の軽いコンポーネントを初期表示するため
export const DummySelect: React.FC<{
  numberOfPeople?: number
  onMouseOn: () => void
  // eslint-disable-next-line react/display-name
}> = React.memo(({ numberOfPeople, onMouseOn }) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  return (
    <div
      className={classes.dummySelect}
      onMouseEnter={onMouseOn}
      onMouseOver={onMouseOn}
    >
      <div>
        {numberOfPeople
          ? `${numberOfPeople}${t('人', { count: numberOfPeople })} `
          : t('なし')}
      </div>
      <ArrowDropDown />
    </div>
  )
})

type ReservationSettingOfTimeCellProps = {
  time: StartEnd
  checked: boolean
  onChangeCheckStatus: (status: DayOfWeekCheckStatus) => void
  reservationTimeFrame?: ReservationTimeFrameProps
  onChange: (reservationTimeFrame: ReservationTimeFrameProps) => void
  maxNumber: number
}

export const ReservationSettingOfTimeCell: React.FC<
  ReservationSettingOfTimeCellProps
> = ({
  time: { start, end },
  checked,
  onChangeCheckStatus,
  reservationTimeFrame,
  onChange,
  maxNumber,
}: ReservationSettingOfTimeCellProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const [onMouse, setOnMouse] = useState<boolean>(false)
  const showSelect = useCallback(() => setOnMouse(true), [])
  const numberOfPeople = reservationTimeFrame?.number_of_people ?? 0
  return (
    <ReservationFrameCell
      className={cx(!numberOfPeople && classes.cellNoSelection)}
    >
      <Checkbox
        color="primary"
        onChange={(_, checked) => onChangeCheckStatus({ [start]: checked })}
        checked={checked}
        className={classes.valueCheckBox}
        icon={<CheckBoxOutlineBlank color="action" />}
      />
      {onMouse ? (
        <Select
          data-testid="select"
          displayEmpty
          defaultValue={0}
          value={numberOfPeople ?? 0}
          onChange={(event) => {
            onChange({
              start_time: start,
              end_time: end,
              number_of_people: event.target.value as number,
            })
          }}
          disableUnderline
          className={cx(
            classes.cellDropdown,
            !numberOfPeople && classes.cellDropdownNoSelection
          )}
        >
          <MenuItem value={0}>{t('なし')}</MenuItem>
          {[...new Array(maxNumber)].map((_, i) => (
            <MenuItem key={i} value={i + 1}>
              {i + 1}
              {t('人', { count: i + 1 })}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <DummySelect numberOfPeople={numberOfPeople} onMouseOn={showSelect} />
      )}
    </ReservationFrameCell>
  )
}
